import { assertNever } from 'assert-never';

import {
	FirmwareUpdateJobRequest,
	RebootJobRequest,
	ScheduledJobRequestDevice,
	ScheduledJobType
} from '@shure/cloud/shared/models/devices';
import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import { ScheduledJobRequestDeviceFragment, ScheduledJobRequestFragment } from '../graphql/generated/cloud-sys-api';

import {
	mapSysApiFirmwareUpdateExecutionStage,
	mapSysApiFirmwareUpdateRequestError,
	mapSysApiFirmwareUpdateRequestStatus
} from './map-firmware-update-requests';
import { mapDeviceModelFromSysApi } from './utils';

function mapScheduledJobRequestDevice(device: ScheduledJobRequestDeviceFragment): ScheduledJobRequestDevice {
	const isKnown = device.__typename === 'Device';

	return {
		known: isKnown,
		nodeId: isKnown ? device.id : undefined,
		hardwareId: device.hardwareId,
		model: isKnown
			? device.interface?.model
				? mapDeviceModelFromSysApi(device.interface.model)
				: DeviceModel.UNKNOWN
			: mapDeviceModelFromSysApi(device.model),
		status: isKnown ? device.status : DeviceStatus.Offline,
		name: isKnown ? (device.features.name?.name ?? undefined) : (device.name ?? undefined),
		roomName: isKnown ? (device.features.room?.room?.name ?? '') : (device.roomName ?? ''),
		currentFirmwareVersion: isKnown
			? (device.features.firmware?.version ?? undefined)
			: (device.firmwareVersion ?? undefined)
	};
}

export function mapRebootJobRequestFromSysApi(
	scheduledJobRequest: ScheduledJobRequestFragment | null | undefined
): RebootJobRequest | undefined {
	if (scheduledJobRequest == null || scheduledJobRequest.__typename !== 'RebootJobRequest') {
		return undefined;
	}

	return {
		id: scheduledJobRequest.id,
		type: ScheduledJobType.Reboot,
		status: scheduledJobRequest.status,
		jobId: scheduledJobRequest.job.jobId,
		executeAt: scheduledJobRequest.job.executeAt,
		task: {
			device: mapScheduledJobRequestDevice(scheduledJobRequest.details.task.device)
		}
	};
}

export function mapFirmwareUpdateJobRequestFromSysApi(
	scheduledJobRequest: ScheduledJobRequestFragment | null | undefined
): FirmwareUpdateJobRequest | undefined {
	if (scheduledJobRequest == null || scheduledJobRequest.__typename !== 'FirmwareUpdateJobRequest') {
		return undefined;
	}
	const result = scheduledJobRequest.details.result;

	return {
		id: scheduledJobRequest.id,
		type: ScheduledJobType.FirmwareUpdate,
		status: scheduledJobRequest.status,
		jobId: scheduledJobRequest.job.jobId,
		executeAt: scheduledJobRequest.job.executeAt,
		task: {
			packageKey: scheduledJobRequest.details.task.packageKey,
			packageVersion: scheduledJobRequest.details.task.packageVersion,
			hosted: scheduledJobRequest.details.task.hosted,
			device: mapScheduledJobRequestDevice(scheduledJobRequest.details.task.device)
		},
		result: result
			? {
					status: mapSysApiFirmwareUpdateRequestStatus(result.status),
					executingStage: mapSysApiFirmwareUpdateExecutionStage(result.stage),
					error: mapSysApiFirmwareUpdateRequestError(result.error),
					progress: result.percentComplete,
					// for convenience, package and version are copied into the result section.
					package: scheduledJobRequest.details.task.packageKey,
					version: scheduledJobRequest.details.task.packageVersion
				}
			: undefined
	};
}

/**
 * Mapper that handles both FirmwareUpdateJobRequest and RebootJobRequest types.
 * Returns the appropriate mapped job request object based on the __typename.
 */
export function mapScheduledJobRequestFromSysApi(
	scheduledJobRequest: ScheduledJobRequestFragment
): FirmwareUpdateJobRequest | RebootJobRequest | undefined {
	switch (scheduledJobRequest.__typename) {
		case 'FirmwareUpdateJobRequest':
			return mapFirmwareUpdateJobRequestFromSysApi(scheduledJobRequest);
		case 'RebootJobRequest':
			return mapRebootJobRequestFromSysApi(scheduledJobRequest);
		default:
			assertNever(scheduledJobRequest);
	}
}
